import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import Close from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ClearIcon from '@mui/icons-material/Clear'
import { Link, useParams } from 'react-router-dom'

import { UUID } from 'crypto'
import LoginSignup from '@/components/LoginSignup'
import { getEffectiveOrgUuid } from '@/utils/utils'
import {
  useApplicationsByNeed,
  useApplications,
} from '@/utils/hooks/applications'
import Loading from '@/components/Loading'

import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import ErrorMessage from '@/components/ErrorMessage'
import NotFound from './NotFound'
import React, { useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { ApplicationTrackingData } from '@/models/Applications'
import { useOrgUsers } from '@/utils/hooks/invitations'
import AccessibleTooltip from '@/components/AccessibleTooltip'
import { enGB } from 'date-fns/locale'
import ApplicationAddModal from '../components/ApplicationAddModal'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { localisedDate } from '@/utils/dateUtils'

interface FormErrors {
  [key: string]: string
}

const convertToUTC = (date: Date | undefined): Date | undefined => {
  if (!date) return undefined
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

const formatDateForInput = (date: Date | undefined): string => {
  if (!date) return ''
  const localDate = new Date(date)
  return format(
    new TZDate(localDate, Intl.DateTimeFormat().resolvedOptions().timeZone),
    'yyyy-MM-dd'
  )
}

const parseInputDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return date
}

// Function to normalize amount input by removing currency symbols and commas
const normalizeAmount = (amount: string): string => {
  // Remove currency symbols, commas, and whitespace
  return amount.replace(/[$,\s]/g, '')
}

const getStatusColor = (status?: string) => {
  if (!status) return {}
  switch (status.toLowerCase()) {
    case 'draft':
      return {
        backgroundColor: '#f5f5f5', // Very light grey
        color: '#424242', // Darker grey text
      }
    case 'submitted':
      return {
        backgroundColor: '#e3f2fd', // Very light blue
        color: '#1976d2', // Medium blue text
      }
    case 'won':
      return {
        backgroundColor: '#e8f5e8', // Very light green
        color: '#1b5e20', // Darker green text
      }
    case 'lost':
      return {
        backgroundColor: '#ffebee', // Very light red/pink
        color: '#c62828', // Medium red text
      }
    default:
      return {}
  }
}

const getStatusLabel = (status?: string) => {
  if (!status) return 'Draft'
  switch (status.toLowerCase()) {
    case 'draft':
      return 'Draft'
    case 'submitted':
      return 'Submitted'
    case 'won':
      return 'Successful'
    case 'lost':
      return 'Unsuccessful'
    default:
      return status
  }
}

export default function ApplicationsListPage() {
  usePageTitle('Applications')
  const { canWrite } = usePausedOrg()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const focusRef = useFocusManagement()

  const [statusFilter, setStatusFilter] = useState<
    'all' | 'draft' | 'submitted' | 'won' | 'lost'
  >('all')
  const [ownerFilter, setOwnerFilter] = useState<'all' | string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const [updateError, setUpdateError] = useState<boolean>(false)
  const [showAddApplicationModal, setShowAddApplicationModal] = useState(false)

  // Editing state for inline editing
  const [editingField, setEditingField] = useState<{
    appId: number | null
    field:
      | 'funds_recieved'
      | 'reporting_date'
      | 'comments'
      | 'owner_uuid'
      | 'status'
      | null
  }>({ appId: null, field: null })
  const [editValue, setEditValue] = useState<
    string | Date | null | number | undefined
  >(null)

  // Edit mode state - which application is in edit mode
  const [editModeAppId, setEditModeAppId] = useState<number | null>(null)

  // Status change to won dialog
  const [statusWonDialog, setStatusWonDialog] = useState(false)
  const [selectedAppForWon, setSelectedAppForWon] =
    useState<ApplicationTrackingData | null>(null)
  const [wonDialogData, setWonDialogData] = useState({
    funds_recieved: '' as string | number,
    reporting_date: null as Date | null,
  })

  const params = useParams()
  const selectedNeed = params.fundingNeedId

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  const { data: orgUsers, isLoading: orgUsersLoading } = useOrgUsers(
    token,
    effectiveOrgUuid
  )

  const {
    data: applications,
    isLoading: applicationsLoading,
    error,
  } = useApplicationsByNeed(
    token as string,
    effectiveOrgUuid,
    Number(selectedNeed)
  )

  const { updateApplicationTracking } = useApplications(
    token as string,
    effectiveOrgUuid,
    Number(selectedNeed)
  )

  const handleEditClick = (
    appId: number,
    field:
      | 'funds_recieved'
      | 'reporting_date'
      | 'comments'
      | 'owner_uuid'
      | 'status',
    currentValue: string | Date | null | number
  ) => {
    setEditingField({ appId, field })
    setErrors({})
    setUpdateError(false)
    if (field === 'reporting_date') {
      currentValue = formatDateForInput(currentValue as Date)
    }
    setEditValue(currentValue)
  }

  const handleStatusChange = (
    newStatus: string,
    app: ApplicationTrackingData,
    funds_recieved: number | null,
    reporting_date: Date | null
  ) => {
    if (newStatus === 'won') {
      setSelectedAppForWon(app)
      setWonDialogData({
        funds_recieved: funds_recieved || '',
        reporting_date: reporting_date,
      })
      setStatusWonDialog(true)
      setEditingField({ appId: null, field: null })
    } else {
      // Handle other status changes normally
      setEditingField({ appId: null, field: null })
      setEditValue(null)
    }
  }

  const handleWonDialogSave = () => {
    if (!selectedAppForWon) {
      return
    }

    // Validate funds_recieved
    const newErrors: FormErrors = {}
    const normalizedAmount = normalizeAmount(
      wonDialogData.funds_recieved as string
    )
    const amountNum = Number(normalizedAmount)

    if (isNaN(amountNum) || amountNum < 0) {
      newErrors.funds_recieved = 'Please enter a valid positive number'
      setErrors(newErrors)
      return
    }

    setErrors({})

    updateApplicationTracking.mutate(
      {
        token: token as string,
        orgUuid: effectiveOrgUuid,
        applicationId: selectedAppForWon.id as number,
        data: {
          status: 'won',
          funds_recieved: amountNum,
          reporting_date: convertToUTC(
            wonDialogData.reporting_date || undefined
          ),
        },
      },
      {
        onError: () => {
          setUpdateError(true)
        },
      }
    )

    setStatusWonDialog(false)
    setSelectedAppForWon(null)
    setWonDialogData({ funds_recieved: '', reporting_date: null })
  }

  const handleSaveEdit = () => {
    const newErrors: FormErrors = {}
    setErrors(newErrors)

    if (
      !editingField.appId ||
      !editingField.field ||
      (editValue === null &&
        editingField.field !== 'comments' &&
        editingField.field !== 'reporting_date')
    ) {
      return
    }

    let processedValue = editValue

    if (
      editingField.field === 'owner_uuid' &&
      (editValue === 'None' || editValue === '')
    ) {
      processedValue = null
    }

    // convert date to UTC
    if (editingField.field === 'reporting_date' && editValue) {
      processedValue = convertToUTC(editValue as Date)
    }

    if (editingField.field === 'funds_recieved') {
      processedValue = normalizeAmount(editValue as string)
      const amountNum = Number(processedValue)

      if (isNaN(amountNum) || amountNum < 0) {
        newErrors.funds_recieved = 'Please enter a valid positive number'
        setErrors(newErrors)
        return
      }

      processedValue = amountNum
    }

    updateApplicationTracking.mutate(
      {
        token: token as string,
        orgUuid: effectiveOrgUuid,
        applicationId: editingField.appId as number,
        data: {
          [editingField.field as string]: processedValue,
        },
      },
      {
        onError: () => {
          setUpdateError(true)
        },
      }
    )

    setEditingField({ appId: null, field: null })
    setEditValue(null)
  }

  const handleCancelEdit = () => {
    setEditingField({ appId: null, field: null })
    setEditValue(null)
    setErrors({})
  }

  const toggleEditMode = (appId: number) => {
    setErrors({})
    setUpdateError(false)
    if (editModeAppId === appId) {
      // Exit edit mode
      setEditModeAppId(null)
      setEditingField({ appId: null, field: null })
      setEditValue(null)
    } else {
      // Enter edit mode
      setEditModeAppId(appId)
    }
  }

  const toggleSortOrder = () => {
    if (sortOrder === null) {
      setSortOrder('asc')
    } else if (sortOrder === 'asc') {
      setSortOrder('desc')
    } else {
      setSortOrder(null)
    }
  }

  if (userLoading || orgsLoading || applicationsLoading || orgUsersLoading) {
    return <Loading />
  }

  if (isError || error || orgErrors.length > 0) {
    return <ErrorMessage />
  }

  if (
    (applications && applications.length === 0) ||
    isNaN(Number(selectedNeed))
  ) {
    return <NotFound />
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!organisation && <ErrorMessage />)}

          {user && token && organisation && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: isMobile ? 'center' : 'flex-start',
                  flexDirection: 'column',
                  width: '100%',
                  margin: '0 auto',
                  pb: { xs: 14, md: 7 },
                }}
              >
                {/* Organisation Details Tab */}

                {applications && selectedNeed && (
                  <Box sx={{ width: '100%', px: 6, mt: 3 }}>
                    <Button
                      component={Link}
                      to={`/applications`}
                      disableRipple
                      sx={{
                        mb: 5,
                        textTransform: 'none',
                        fontSize: '18px',
                        color: '#1C3045',
                        width: isMobile ? '55vw' : '400px',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        fontWeight: 300,
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                    >
                      <WestIcon
                        sx={{
                          mr: isMobile ? '5%' : '5%',
                          fontSize: '18px',
                        }}
                      />
                      Back to Funding Request List
                    </Button>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 3 }}
                    >
                      <Typography
                        variant="h1"
                        component="h1"
                        ref={focusRef}
                        tabIndex={-1}
                        sx={{
                          mb: 1,
                          textAlign: 'left',
                          fontSize: '34px',
                          '&:focus': {
                            outline: 'none',
                          },
                        }}
                      >
                        Applications for {applications[0].funding_need_title}
                      </Typography>

                      {/* IMPLEMENT LATER - SHOW APPLICATION TRACKING */}
                      {/*
                      <Link to="/tracking" style={{ textDecoration: 'none' }}>
                        <Button
                        disableRipple
                          variant="outlined"
                          sx={{
                            textTransform: 'none',
                            borderColor: '#1C3045',
                            color: '#1C3045',
                            '&:hover': {
                              borderColor: '#1C3045',
                              backgroundColor: '#f7f7f7',
                            },
                          }}
                        >
                          View All Tracking
                        </Button>
                      </Link> */}
                    </Box>
                    {/* Filter Controls */}
                    <Box
                      sx={{
                        mb: 3,
                        display: 'flex',
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                      }}
                    >
                      <TextField
                        size="small"
                        label="Search Applications"
                        placeholder="Search by funder or fund name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <SearchIcon
                                sx={{ color: 'text.secondary', mr: 1 }}
                                aria-hidden="true"
                              />
                            ),
                          },
                        }}
                        sx={{
                          minWidth: isMobile ? '100%' : '350px',
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#757575',
                          },
                        }}
                      />

                      <FormControl
                        size="small"
                        sx={{ minWidth: isMobile ? '100%' : '200px' }}
                      >
                        <InputLabel id="status-filter-label">
                          Filter by Status
                        </InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) =>
                            setStatusFilter(
                              e.target.value as typeof statusFilter
                            )
                          }
                          label="Filter by Status"
                          labelId="status-filter-label"
                          inputProps={{
                            'aria-labelledby': 'status-filter-label',
                          }}
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#757575',
                            },
                          }}
                        >
                          <MenuItem value="all">All Statuses</MenuItem>
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="submitted">Submitted</MenuItem>
                          <MenuItem value="won">Successful</MenuItem>
                          <MenuItem value="lost">Unsuccessful</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl
                        size="small"
                        sx={{ minWidth: isMobile ? '100%' : '200px' }}
                      >
                        <InputLabel id="owner-filter-label">
                          Filter by Owner
                        </InputLabel>
                        <Select
                          value={ownerFilter}
                          onChange={(e) =>
                            setOwnerFilter(e.target.value as typeof ownerFilter)
                          }
                          label="Filter by Owner"
                          labelId="owner-filter-label"
                          inputProps={{
                            'aria-labelledby': 'owner-filter-label',
                          }}
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#757575',
                            },
                          }}
                        >
                          <MenuItem value="all">All Owners</MenuItem>

                          {orgUsers &&
                            orgUsers.length > 0 &&
                            orgUsers
                              .filter(
                                (user) => user.first_name && user.last_name
                              )
                              .map((users) => (
                                <MenuItem key={users.uuid} value={users.uuid}>
                                  {users.first_name} {users.last_name}
                                </MenuItem>
                              ))}
                        </Select>
                      </FormControl>

                      {(statusFilter !== 'all' ||
                        ownerFilter !== 'all' ||
                        searchTerm) && (
                        <Button
                          disableRipple
                          size="small"
                          onClick={() => {
                            setStatusFilter('all')
                            setOwnerFilter('all')
                            setSearchTerm('')
                          }}
                          sx={{ minWidth: 'auto', textTransform: 'none' }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                    {/* <Box
                      sx={{
                        mt: isMobile ? 6 : 0,
                        mb: -2,
                        display: 'flex',
                        width: '100%',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                      }}
                    >
                      <Button
                        disableRipple
                        onClick={() => {
                          setShowAddApplicationModal(true)
                        }}
                        sx={{
                          textTransform: 'none',
                        }}
                      >
                        <AddIcon sx={{ mr: 1 }} /> Add a new application to
                        track
                      </Button>
                    </Box>

                    {showAddApplicationModal && (
                      <ApplicationAddModal
                        isOpen={showAddApplicationModal}
                        onClose={() => setShowAddApplicationModal(false)}
                        orgUuid={effectiveOrgUuid}
                        needId={selectedNeed}
                        orgUsers={orgUsers}
                        token={token}
                      />
                    )} */}
                    {updateError && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                        }}
                      >
                        Sorry, something went wrong updating the application.
                        Please try again later.
                      </Alert>
                    )}

                    <TableContainer
                      sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        position: 'relative',
                        overflow: 'auto',
                        maxHeight: 600,
                        mt: 6,
                        mb: 8,
                      }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                width: '35%',
                                fontWeight: 500,
                                color: '#1C3045',
                                py: 2,
                              }}
                              aria-label="Application Details"
                            >
                              <span style={{ fontWeight: 500 }}></span>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              Owner
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              Funds Sorted
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: 1,
                                }}
                              >
                                Reporting Date
                                <span
                                  aria-live="polite"
                                  aria-atomic="true"
                                  style={{
                                    position: 'absolute',
                                    left: '-10000px',
                                    width: '1px',
                                    height: '1px',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {sortOrder === 'asc'
                                    ? 'Sorted by reporting date ascending'
                                    : sortOrder === 'desc'
                                    ? 'Sorted by reporting date descending'
                                    : 'Not sorted'}
                                </span>
                                <IconButton
                                  disableRipple
                                  size="small"
                                  onClick={toggleSortOrder}
                                  aria-label={
                                    sortOrder === 'asc'
                                      ? 'Sort reporting date descending'
                                      : sortOrder === 'desc'
                                      ? 'Clear sorting'
                                      : 'Sort reporting date ascending'
                                  }
                                  sx={{
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    px: 0.5,
                                    borderRadius: 1,
                                    color: sortOrder
                                      ? '#1C3045'
                                      : 'text.secondary',
                                    '&:focus': {
                                      bgcolor: 'grey.300',
                                    },
                                    '&:hover': {
                                      bgcolor: 'grey.200',
                                    },
                                  }}
                                >
                                  {sortOrder === 'asc' ? (
                                    <ArrowUpwardIcon
                                      sx={{ fontSize: '16px' }}
                                    />
                                  ) : sortOrder === 'desc' ? (
                                    <ArrowDownwardIcon
                                      sx={{ fontSize: '16px' }}
                                    />
                                  ) : (
                                    <>
                                      <ArrowUpwardIcon
                                        sx={{ fontSize: '14px', opacity: 0.5 }}
                                      />
                                      <span
                                        style={{
                                          fontSize: '0.7rem',
                                          marginLeft: '2px',
                                        }}
                                      >
                                        Sort
                                      </span>
                                    </>
                                  )}
                                </IconButton>
                                {sortOrder && (
                                  <IconButton
                                    disableRipple
                                    size="small"
                                    onClick={() => setSortOrder(null)}
                                    aria-label="Clear sorting"
                                    sx={{
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      px: 0.5,
                                      borderRadius: 1,
                                      color: 'text.secondary',
                                      '&:focus': {
                                        bgcolor: 'grey.300',
                                      },
                                      '&:hover': {
                                        bgcolor: 'grey.200',
                                      },
                                    }}
                                  >
                                    Clear
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: '#1C3045',
                                width: '5%',
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {applications

                            // Apply sorting before filtering
                            .sort((a, b) => {
                              if (!sortOrder) return a.id - b.id // Keep original order if no sort

                              const dateA = a.reporting_date
                                ? new Date(a.reporting_date)
                                : null
                              const dateB = b.reporting_date
                                ? new Date(b.reporting_date)
                                : null

                              // Handle null dates - put them at the end
                              if (!dateA && !dateB) return 0
                              if (!dateA) return 1
                              if (!dateB) return -1

                              // Sort by date

                              const comparison =
                                dateA.getTime() - dateB.getTime()
                              return sortOrder === 'asc'
                                ? comparison
                                : -comparison
                            })
                            .map((app) => {
                              const { status, owner_uuid } = app

                              // Apply filters
                              const matchesStatusFilter =
                                statusFilter === 'all' ||
                                status === statusFilter
                              const matchesOwnerFilter =
                                ownerFilter === 'all' ||
                                owner_uuid === ownerFilter

                              // Search filter (case insensitive search in funder name and fund name)
                              const matchesSearch =
                                searchTerm === '' ||
                                app.funder_name
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()) ||
                                (app.fund_name &&
                                  app.fund_name
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase()))

                              if (
                                !matchesStatusFilter ||
                                !matchesOwnerFilter ||
                                !matchesSearch
                              ) {
                                return null
                              }

                              return (
                                <React.Fragment key={app.id}>
                                  <TableRow>
                                    {/* Application Details */}
                                    <TableCell
                                      sx={{
                                        py: 2,
                                        width: '35%',
                                      }}
                                    >
                                      <Box
                                        component={
                                          app.has_answers ? Link : 'div'
                                        }
                                        to={`/applications/${selectedNeed}/${app.id}`}
                                        sx={{
                                          textDecoration: 'none',
                                          color: '#1C3045',
                                          width: '100%',
                                          display: 'block',
                                          p: 1,
                                          borderRadius: 1,
                                          '&:hover': {
                                            backgroundColor: '#fafafa',
                                          },
                                        }}
                                      >
                                        <Box display={'flex'}>
                                          {app.has_answers &&
                                          app.questions_outdated ? (
                                            app.status === 'draft' && (
                                              <AccessibleTooltip title="This application's questions have been updated. Please click through to view and update your application.">
                                                <span
                                                  role="img"
                                                  aria-label="Warning: This application's questions have been updated. Please click through to view and update your application."
                                                  tabIndex={0}
                                                >
                                                  <WarningAmberIcon
                                                    sx={{
                                                      mr: 1,
                                                      color: 'orange',
                                                    }}
                                                  />
                                                </span>
                                              </AccessibleTooltip>
                                            )
                                          ) : app.has_answers &&
                                            app.has_unanswered_questions &&
                                            app.status === 'draft' ? (
                                            <AccessibleTooltip title="Some questions in this application have not been answered. Please click through to view and update your application.">
                                              <span
                                                role="img"
                                                aria-label="Warning: Some questions in this application have not been answered. Please click through to view and update your application."
                                                tabIndex={0}
                                              >
                                                <WarningAmberIcon
                                                  sx={{
                                                    mr: 1,
                                                    color: 'orange',
                                                  }}
                                                />
                                              </span>
                                            </AccessibleTooltip>
                                          ) : null}
                                          <Typography
                                            variant="body1"
                                            fontWeight={500}
                                          >
                                            {app.funder_name}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2">
                                          {app.fund_name
                                            ? app.fund_name
                                            : 'No Fund Name'}
                                        </Typography>

                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Application Last Updated:{' '}
                                          {localisedDate(
                                            new Date(app.updated_at)
                                          )}
                                        </Typography>
                                      </Box>

                                      {/* Notes Section */}
                                      <Box sx={{ mt: 1 }}>
                                        {editingField.appId === app.id &&
                                        editingField.field === 'comments' ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: 1,
                                            }}
                                          >
                                            <TextField
                                              size="small"
                                              multiline
                                              rows={2}
                                              value={editValue || ''}
                                              onChange={(e) =>
                                                setEditValue(e.target.value)
                                              }
                                              placeholder="Add notes..."
                                              sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-notchedOutline':
                                                  {
                                                    borderColor: '#757575',
                                                  },
                                              }}
                                              autoFocus
                                            />
                                            <Box>
                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleSaveEdit}
                                                sx={{
                                                  textTransform: 'none',
                                                  mr: 1,
                                                }}
                                              >
                                                Save
                                              </Button>

                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleCancelEdit}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Cancel
                                              </Button>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mb: 0.5,
                                              }}
                                            >
                                              <IconButton
                                                disableRipple
                                                size="small"
                                                disabled={!canWrite}
                                                onClick={() =>
                                                  handleEditClick(
                                                    app.id,
                                                    'comments',
                                                    app.comments || ''
                                                  )
                                                }
                                                sx={{
                                                  minWidth: 'auto',
                                                  p: 0.25,
                                                  '&:focus': {
                                                    bgcolor: 'grey.300',
                                                  },
                                                  '&:hover': {
                                                    bgcolor: 'grey.200',
                                                  },
                                                }}
                                                aria-label="Edit notes"
                                              >
                                                <EditOutlinedIcon
                                                  sx={{
                                                    fontSize: '16px',
                                                    color: 'primary.main',
                                                  }}
                                                />
                                              </IconButton>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                Notes:
                                              </Typography>
                                            </Box>
                                            {app.comments ? (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.875rem',
                                                  ml: 3,
                                                }}
                                              >
                                                {app.comments}
                                              </Typography>
                                            ) : (
                                              <Typography
                                                variant="body2"
                                                color="#666666"
                                                sx={{
                                                  fontStyle: 'italic',
                                                  fontSize: '0.875rem',
                                                  ml: 3,
                                                }}
                                              >
                                                No notes
                                              </Typography>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>

                                    {/* Owner */}
                                    <TableCell
                                      sx={{
                                        textAlign: 'center',
                                        py: 2,
                                        width: '15%',
                                      }}
                                    >
                                      {editingField.appId === app.id &&
                                      editingField.field === 'owner_uuid' ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                          }}
                                        >
                                          <Select
                                            size="small"
                                            value={editValue ?? 'None'}
                                            onChange={(e) =>
                                              setEditValue(e.target.value)
                                            }
                                            sx={{ minWidth: '120px' }}
                                            autoFocus
                                          >
                                            <MenuItem value="None">
                                              No owner
                                            </MenuItem>
                                            {orgUsers &&
                                              orgUsers.length > 0 &&
                                              orgUsers
                                                .filter(
                                                  (user) =>
                                                    user.first_name &&
                                                    user.last_name
                                                )
                                                .map((users) => (
                                                  <MenuItem
                                                    key={users.uuid}
                                                    value={users.uuid}
                                                  >
                                                    {users.first_name}{' '}
                                                    {users.last_name}
                                                  </MenuItem>
                                                ))}
                                          </Select>
                                          <Box>
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={handleSaveEdit}
                                              sx={{
                                                textTransform: 'none',
                                                mr: 1,
                                              }}
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={handleCancelEdit}
                                              sx={{ textTransform: 'none' }}
                                            >
                                              Cancel
                                            </Button>
                                          </Box>
                                        </Box>
                                      ) : (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <Typography variant="body2">
                                            <span
                                              style={{
                                                fontStyle: app.owner_name
                                                  ? 'normal'
                                                  : 'italic',
                                                color: app.owner_name
                                                  ? 'inherit'
                                                  : '#666666',
                                              }}
                                            >
                                              {app.owner_name || 'No owner'}
                                            </span>
                                          </Typography>
                                          {editModeAppId === app.id && (
                                            <IconButton
                                              disableRipple
                                              size="small"
                                              onClick={() =>
                                                handleEditClick(
                                                  app.id,
                                                  'owner_uuid',
                                                  app.owner_uuid || ''
                                                )
                                              }
                                              sx={{
                                                minWidth: 'auto',
                                                p: 0.5,
                                                '&:focus': {
                                                  bgcolor: 'grey.300',
                                                },
                                                '&:hover': {
                                                  bgcolor: 'grey.200',
                                                },
                                              }}
                                              aria-label="Edit owner"
                                            >
                                              <EditOutlinedIcon
                                                sx={{
                                                  fontSize: '16px',
                                                  color: 'primary.main',
                                                }}
                                              />
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}
                                    </TableCell>

                                    {/* Status Chip */}
                                    <TableCell
                                      sx={{
                                        textAlign: 'center',
                                        py: 2,
                                        width: '15%',
                                      }}
                                    >
                                      {editingField.appId === app.id &&
                                      editingField.field === 'status' ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                          }}
                                        >
                                          <Select
                                            size="small"
                                            value={
                                              (editValue as string) ||
                                              app.status
                                            }
                                            error={!!errors.status}
                                            onChange={(e) => {
                                              const newStatus = e.target
                                                .value as string
                                              setEditValue(newStatus)
                                              if (
                                                newStatus === 'won' &&
                                                app.status !== 'won'
                                              ) {
                                                handleStatusChange(
                                                  newStatus,
                                                  app,
                                                  app.funds_recieved || 0,
                                                  app.reporting_date || null
                                                )
                                              }
                                            }}
                                            sx={{ minWidth: '120px' }}
                                            autoFocus
                                          >
                                            <MenuItem value="draft">
                                              Draft
                                            </MenuItem>
                                            <MenuItem value="submitted">
                                              Submitted
                                            </MenuItem>
                                            <MenuItem value="won">
                                              Successful
                                            </MenuItem>
                                            <MenuItem value="lost">
                                              Unsuccessful
                                            </MenuItem>
                                          </Select>
                                          {editValue !== 'won' && (
                                            <Box>
                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleSaveEdit}
                                                sx={{
                                                  textTransform: 'none',
                                                  mr: 1,
                                                }}
                                              >
                                                Save
                                              </Button>
                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleCancelEdit}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Cancel
                                              </Button>
                                            </Box>
                                          )}
                                        </Box>
                                      ) : (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <Chip
                                            label={getStatusLabel(app.status)}
                                            sx={getStatusColor(app.status)}
                                            size="small"
                                          />
                                          {editModeAppId === app.id && (
                                            <IconButton
                                              disableRipple
                                              size="small"
                                              onClick={() =>
                                                handleEditClick(
                                                  app.id,
                                                  'status',
                                                  app.status || 'draft'
                                                )
                                              }
                                              sx={{
                                                minWidth: 'auto',
                                                p: 0.5,
                                                '&:focus': {
                                                  bgcolor: 'grey.300',
                                                },
                                                '&:hover': {
                                                  bgcolor: 'grey.200',
                                                },
                                              }}
                                              aria-label="Edit status"
                                            >
                                              <EditOutlinedIcon
                                                sx={{
                                                  fontSize: '16px',
                                                  color: 'primary.main',
                                                }}
                                              />
                                            </IconButton>
                                          )}
                                        </Box>
                                      )}
                                    </TableCell>

                                    {/* Amount Granted */}
                                    <TableCell
                                      sx={{
                                        textAlign: 'center',
                                        py: 2,
                                        width: '15%',
                                      }}
                                    >
                                      {editingField.appId === app.id &&
                                      editingField.field ===
                                        'funds_recieved' ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexDirection: 'column',
                                          }}
                                        >
                                          <TextField
                                            size="small"
                                            value={editValue || ''}
                                            onChange={(e) =>
                                              setEditValue(e.target.value)
                                            }
                                            error={!!errors.funds_recieved}
                                            helperText={errors.funds_recieved}
                                            placeholder="$0"
                                            sx={{
                                              width: '150px',
                                              '& .MuiOutlinedInput-notchedOutline':
                                                {
                                                  borderColor: '#757575',
                                                },
                                            }}
                                            autoFocus
                                          />
                                          <Box>
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={handleSaveEdit}
                                              disabled={!editValue}
                                              sx={{ textTransform: 'none' }}
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={handleCancelEdit}
                                              sx={{ textTransform: 'none' }}
                                            >
                                              Cancel
                                            </Button>
                                          </Box>
                                        </Box>
                                      ) : app.status === 'won' ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          {app.funds_recieved ? (
                                            <>
                                              <Typography
                                                variant="body2"
                                                sx={{ color: '#1C3045' }}
                                              >
                                                ${app.funds_recieved}
                                              </Typography>
                                              {editModeAppId === app.id && (
                                                <IconButton
                                                  disableRipple
                                                  size="small"
                                                  onClick={() =>
                                                    handleEditClick(
                                                      app.id,
                                                      'funds_recieved',
                                                      app.funds_recieved || 0
                                                    )
                                                  }
                                                  sx={{
                                                    minWidth: 'auto',
                                                    p: 0.5,
                                                    '&:focus': {
                                                      bgcolor: 'grey.300',
                                                    },
                                                    '&:hover': {
                                                      bgcolor: 'grey.200',
                                                    },
                                                  }}
                                                  aria-label="Edit funds recieved amount"
                                                >
                                                  <EditOutlinedIcon
                                                    sx={{
                                                      fontSize: '18px',
                                                      color: 'primary.main',
                                                    }}
                                                  />
                                                </IconButton>
                                              )}
                                            </>
                                          ) : editModeAppId === app.id ? (
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={() =>
                                                handleEditClick(
                                                  app.id,
                                                  'funds_recieved',
                                                  ''
                                                )
                                              }
                                              startIcon={<AddIcon />}
                                              sx={{
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                              }}
                                            >
                                              Add Amount
                                            </Button>
                                          ) : null}
                                        </Box>
                                      ) : null}
                                    </TableCell>

                                    {/* Reporting Date */}
                                    <TableCell
                                      sx={{
                                        textAlign: 'center',
                                        py: 2,
                                        width: '15%',
                                      }}
                                    >
                                      {editingField.appId === app.id &&
                                      editingField.field ===
                                        'reporting_date' ? (
                                        <LocalizationProvider
                                          dateAdapter={AdapterDateFns}
                                          adapterLocale={enGB}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              flexDirection: 'column',
                                            }}
                                          >
                                            <DatePicker
                                              value={
                                                editValue
                                                  ? typeof editValue ===
                                                    'string'
                                                    ? parseInputDate(editValue)
                                                    : (editValue as Date)
                                                  : null
                                              }
                                              onChange={(date) =>
                                                setEditValue(date)
                                              }
                                              format="dd/MM/yyyy"
                                              slotProps={{
                                                textField: {
                                                  size: 'small',
                                                  sx: {
                                                    width: '170px',
                                                    '& .MuiOutlinedInput-notchedOutline':
                                                      {
                                                        borderColor: '#757575',
                                                      },
                                                  },
                                                  'aria-labelledby':
                                                    'reporting-date',
                                                  InputProps: {
                                                    endAdornment: editValue && (
                                                      <IconButton
                                                        sx={{ padding: 0 }}
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.preventDefault()
                                                          e.stopPropagation()
                                                          setEditValue(null)
                                                        }}
                                                        aria-label="Clear date"
                                                      >
                                                        <ClearIcon />
                                                      </IconButton>
                                                    ),
                                                  },
                                                },
                                              }}
                                            />
                                            <Box>
                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleSaveEdit}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Save
                                              </Button>
                                              <Button
                                                disableRipple
                                                size="small"
                                                onClick={handleCancelEdit}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Cancel
                                              </Button>
                                            </Box>
                                          </Box>
                                        </LocalizationProvider>
                                      ) : app.status === 'won' ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          {app.reporting_date ? (
                                            <>
                                              <Typography variant="body2">
                                                {format(
                                                  app.reporting_date,
                                                  'dd MMMM yyyy'
                                                )}
                                              </Typography>
                                              {editModeAppId === app.id && (
                                                <IconButton
                                                  disableRipple
                                                  size="small"
                                                  onClick={() =>
                                                    handleEditClick(
                                                      app.id,
                                                      'reporting_date',
                                                      app.reporting_date || null
                                                    )
                                                  }
                                                  sx={{
                                                    minWidth: 'auto',
                                                    p: 0.5,
                                                    '&:focus': {
                                                      bgcolor: 'grey.300',
                                                    },
                                                    '&:hover': {
                                                      bgcolor: 'grey.200',
                                                    },
                                                  }}
                                                  aria-label="Edit reporting date"
                                                >
                                                  <EditOutlinedIcon
                                                    sx={{
                                                      fontSize: '18px',
                                                      color: 'primary.main',
                                                    }}
                                                  />
                                                </IconButton>
                                              )}
                                            </>
                                          ) : editModeAppId === app.id ? (
                                            <Button
                                              disableRipple
                                              size="small"
                                              onClick={() =>
                                                handleEditClick(
                                                  app.id,
                                                  'reporting_date',
                                                  null
                                                )
                                              }
                                              startIcon={<AddIcon />}
                                              sx={{
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                              }}
                                            >
                                              Add Reporting Date
                                            </Button>
                                          ) : null}
                                        </Box>
                                      ) : null}
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell
                                      sx={{
                                        textAlign: 'left',
                                        py: 2,
                                        width: '5%',
                                      }}
                                    >
                                      <Button
                                        disableRipple
                                        size="small"
                                        disabled={!canWrite}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          toggleEditMode(app.id)
                                        }}
                                        startIcon={
                                          editModeAppId === Number(app.id) ? (
                                            <Close />
                                          ) : (
                                            <EditIcon />
                                          )
                                        }
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '0.75rem',
                                          color: '#1C3045',
                                          '&:hover': {
                                            bgcolor: 'grey.200',
                                          },
                                          '&:focus': {
                                            bgcolor: 'grey.300',
                                          },
                                        }}
                                      >
                                        {editModeAppId === Number(app.id)
                                          ? 'Cancel Edit'
                                          : 'Edit Details'}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              )
                            })
                            .filter(Boolean)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </>
          )}
        </>
      </Authenticated>

      {/* Mark as Won Dialog */}
      <Dialog
        open={statusWonDialog}
        onClose={() => setStatusWonDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Application as Successful</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedAppForWon && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6">
                  {selectedAppForWon.funder_name}
                </Typography>
                <Typography color="text.secondary">
                  {selectedAppForWon.fund_name || 'No Fund Name'}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Amount Granted"
              value={wonDialogData.funds_recieved}
              onChange={(e) =>
                setWonDialogData({
                  ...wonDialogData,
                  funds_recieved: e.target.value,
                })
              }
              error={!!errors.funds_recieved}
              helperText={errors.funds_recieved}
              placeholder="$0"
              sx={{ mb: 2 }}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Reporting Due Date"
                value={
                  wonDialogData.reporting_date
                    ? typeof wonDialogData.reporting_date === 'string'
                      ? parseInputDate(wonDialogData.reporting_date)
                      : wonDialogData.reporting_date
                    : null
                }
                onChange={(date) =>
                  setWonDialogData({ ...wonDialogData, reporting_date: date })
                }
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    InputProps: {
                      endAdornment: wonDialogData.reporting_date && (
                        <IconButton
                          sx={{ padding: 0 }}
                          size="small"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setWonDialogData({
                              ...wonDialogData,
                              reporting_date: null,
                            })
                          }}
                          aria-label="Clear date"
                        >
                          <ClearIcon />
                        </IconButton>
                      ),
                    },
                  },
                }}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disableRipple onClick={() => setStatusWonDialog(false)}>
            Cancel
          </Button>
          <Button
            disableRipple
            onClick={handleWonDialogSave}
            variant="contained"
          >
            Mark as Successful
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
